import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { documentService, userService } from '../../services/api';
import { FiX, FiUploadCloud } from 'react-icons/fi';
import './DocumentUpload.css';

const DOCUMENT_TYPES = [
  { id: 'address-proof', label: 'Address Proof' },
  { id: 'id-proof', label: 'ID Proof' },
  { id: 'passport', label: 'Passport' },
  { id: 'visa', label: 'Visa' },
  { id: 'other', label: 'Other' },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

export const DocumentUpload = ({ onClose, onSuccess, onError, targetUserId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const fileInputRef = useRef(null);
  const dragAreaRef = useRef(null);

  const [formData, setFormData] = useState({
    documentType: 'other',
    expiryDate: '',
    notes: '',
    userId: targetUserId || '',
  });

  const [employees, setEmployees] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  React.useEffect(() => {
    if (isAdmin && !targetUserId) {
      userService.getAll({ isActive: 'all', limit: 1000 }).then(res => {
        setEmployees(res.data.users || []);
      }).catch(() => {});
    }
  }, [isAdmin, targetUserId]);

  const validateFile = (selectedFile) => {
    if (!selectedFile) {
      setError('Please select a file');
      return false;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return false;
    }

    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError('File type not allowed. Please upload PDF, JPG, PNG, or Word documents.');
      return false;
    }

    return true;
  };

  const handleFileSelect = (selectedFile) => {
    if (validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleFileInput = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!formData.documentType) {
      setError('Please select a document type');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('documentType', formData.documentType);
      uploadFormData.append('expiryDate', formData.expiryDate);
      uploadFormData.append('notes', formData.notes);
      if (isAdmin && formData.userId) {
        uploadFormData.append('userId', formData.userId);
      }

      await documentService.upload(uploadFormData);

      onSuccess();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to upload document';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      console.error(err);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="document-upload-overlay">
      <div className="document-upload-modal">
        <div className="modal-header">
          <h2>Upload Document</h2>
          <button
            className="btn-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="documentType">Document Type *</label>
            <select
              id="documentType"
              name="documentType"
              value={formData.documentType}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              {DOCUMENT_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {isAdmin && !targetUserId && (
            <div className="form-group">
              <label htmlFor="userId">Upload For Employee</label>
              <select
                id="userId"
                name="userId"
                value={formData.userId}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">Myself</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName} {!emp.isActive ? '(Ex-Employee)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>File *</label>
            {!file ? (
              <div
                ref={dragAreaRef}
                className={`drag-drop-area ${isDragging ? 'dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <FiUploadCloud className="upload-icon" />
                <div className="drag-text">
                  <p className="drag-title">Drag and drop your file here</p>
                  <p className="drag-subtitle">or</p>
                </div>
                <button
                  type="button"
                  className="btn-browse"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                >
                  Browse Files
                </button>
                <p className="file-info">
                  Supported: PDF, JPG, PNG, Word (Max 10MB)
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileInput}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  style={{ display: 'none' }}
                  disabled={loading}
                />
              </div>
            ) : (
              <div className="file-selected">
                <div className="file-info-box">
                  <div className="file-details">
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-file"
                    onClick={handleRemoveFile}
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="progress-text">{uploadProgress}%</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expiryDate">Expiry Date (Optional)</label>
              <input
                id="expiryDate"
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Add any notes about this document"
              rows={3}
              disabled={loading}
            ></textarea>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !file}
            >
              {loading ? `Uploading... ${uploadProgress}%` : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUpload;
